import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  faCity,
  faHeart,
  faHouseChimneyCrack,
} from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import Button from '../../components/button/Button'
import Icon from '../../components/icon/Icon'
import InquireLauncher from '../../components/inquire-launcher/InquireLauncher'
import MatchScoreBadge from '../../components/match-score-badge/MatchScoreBadge'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { DiscoverFilters } from '../../_types/quickpass-discover'
import { getDiscoverBuilding, trackDiscoverView } from '../../_data/discover'
import { DiscoverBuildingUnit } from '../../_types/quickpass-discover'
import { buildPrefilledBookingUrl } from '../../_utilities/bookingPrefill'
import { isEmbeddedExperience } from '../../_utilities/embed-context'
import {
  decodeBuildingKey,
  formatBuildingBeds,
  formatBuildingPriceRange,
  formatUnitBedsBaths,
  formatUnitPrice,
  getBuildingStatusLabel,
  getUnitStatusLabel,
} from './helpers'
import { DiscoverDetailPageState } from './types'
import {
  Address,
  BackLink,
  Description,
  GalleryRoot,
  HeaderActions,
  HeaderInfo,
  HeaderRow,
  ImagePlaceholder,
  InquireActions,
  InquireSummary,
  InquireSummaryMeta,
  InquireSummaryTitle,
  MainImage,
  MainImageFrame,
  MetaPill,
  MetaPills,
  NotFoundCard,
  PageStack,
  PriceBlock,
  PrimaryRow,
  SectionCard,
  SectionTitle,
  StatusBadge,
  StickyInquireBar,
  TagChip,
  TagGrid,
  Thumbnail,
  ThumbnailImage,
  ThumbnailRow,
  Title,
  UnitActions,
  UnitInfo,
  UnitList,
  UnitMeta,
  UnitPrice,
  UnitRow,
  UnitStatusBadge,
  UnitTitle,
} from './styled'

const initialiseState = (): DiscoverDetailPageState => ({
  building: null,
  isLoading: true,
  isError: false,
  activeImageIndex: 0,
  isInquireOpen: false,
  inquireUnit: null,
})

/**
 * Building-level detail surface for /discover/:buildingKey. Renders the
 * gallery + building summary up top, then a list of every matching unit
 * underneath. Renters inquire on a specific unit (the inquire modal
 * carries the selected unit's id along with the building snapshot).
 *
 * The URL param accepts new building keys (`building:abc`,
 * `community:def`, `inventory:xyz`) and legacy raw inventory ids — the
 * server's `discovergetbuilding` resolves all three shapes.
 *
 * @returns {JSX.Element}
 */
const DiscoverDetailPage = (): JSX.Element => {
  const { listingId } = useParams<{ listingId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { quickPassUser } = useAuth()
  const { profile, saveListingToProfile, unsaveListingFromProfile } =
    useQuickPassProfile()
  const [state, setState] = useState<DiscoverDetailPageState>(initialiseState)
  const [togglingSaveId, setTogglingSaveId] = useState<string | null>(null)

  const buildingKey = useMemo<string | null>(() => {
    if (listingId === undefined || listingId === '') return null
    return decodeBuildingKey(listingId)
  }, [listingId])

  const incomingFilters = useMemo<DiscoverFilters | null>(() => {
    const routerState = location.state as
      | { filters?: DiscoverFilters }
      | null
      | undefined
    return routerState?.filters ?? null
  }, [location.state])

  const savedSet = useMemo<Set<string>>(
    () => new Set<string>(profile?.savedListingIds ?? []),
    [profile?.savedListingIds],
  )

  useEffect(() => {
    if (buildingKey === null) {
      setState(previous => ({ ...previous, isLoading: false, isError: true }))
      return
    }

    let isMounted = true

    const load = async (): Promise<void> => {
      setState(previous => ({
        ...previous,
        isLoading: true,
        isError: false,
      }))

      const fetched = await getDiscoverBuilding({
        buildingKey,
        quickPassUserId: quickPassUser?.id ?? null,
        filters: incomingFilters,
      })

      if (isMounted === false) return

      setState(previous => ({
        ...previous,
        building: fetched,
        isLoading: false,
        isError: fetched === null,
        activeImageIndex: 0,
      }))
    }

    void load()

    return () => {
      isMounted = false
    }
  }, [buildingKey, quickPassUser?.id, incomingFilters])

  useEffect(() => {
    if (
      state.building === null ||
      quickPassUser === null ||
      buildingKey === null
    ) {
      return
    }
    void trackDiscoverView({
      quickPassUserId: quickPassUser.id,
      inventoryId: buildingKey,
    })
  }, [state.building, quickPassUser, buildingKey])

  const handleBookTour = useCallback((): void => {
    const building = state.building
    if (building === null || !building.tourBooking) {
      return
    }
    const fullName =
      `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() ||
      (quickPassUser?.displayName ?? '')
    const url = buildPrefilledBookingUrl(
      building.tourBooking.agentUserId,
      building.tourBooking.eventTypeSlug,
      {
        quickPassUserId: quickPassUser?.id ?? '',
        name: fullName,
        email: quickPassUser?.email ?? '',
      },
    )
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [state.building, profile, quickPassUser])

  const handleOpenInquire = useCallback(
    (unit: DiscoverBuildingUnit | null): void => {
      if (state.building === null) return
      setState(previous => ({
        ...previous,
        isInquireOpen: true,
        inquireUnit: unit,
      }))
    },
    [state.building],
  )

  const handleCloseInquire = useCallback((): void => {
    setState(previous => ({ ...previous, isInquireOpen: false }))
  }, [])

  const handleUnitSaveToggle = useCallback(
    async (unit: DiscoverBuildingUnit): Promise<void> => {
      if (togglingSaveId !== null) return
      setTogglingSaveId(unit.inventoryId)
      try {
        if (savedSet.has(unit.inventoryId) === true) {
          await unsaveListingFromProfile(unit.inventoryId)
        } else {
          await saveListingToProfile(unit.inventoryId)
        }
      } catch (error) {
        console.error('[discover-detail] save toggle failed', error)
      } finally {
        setTogglingSaveId(null)
      }
    },
    [savedSet, saveListingToProfile, unsaveListingFromProfile, togglingSaveId],
  )

  const isEmbedded = isEmbeddedExperience()
  const backTo = isEmbedded ? '/saved-listings' : '/discover'
  const backLabel = isEmbedded ? '← Back to saved' : '← Back to discover'

  if (state.isLoading === true) {
    return (
      <AppLayout contentMaxWidth="wide">
        <PageStack>
          <BackLink to={backTo}>{backLabel}</BackLink>
          <NotFoundCard>Loading building…</NotFoundCard>
        </PageStack>
      </AppLayout>
    )
  }

  if (state.isError === true || state.building === null) {
    return (
      <AppLayout contentMaxWidth="wide">
        <PageStack>
          <BackLink to={backTo}>{backLabel}</BackLink>
          <NotFoundCard>
            <Icon icon={faHouseChimneyCrack} label="House" />
            <h2>Building not available</h2>
            <p>
              This one might have been pulled, archived, or you reached it from
              a stale link. Head back and try another.
            </p>
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate(backTo)}
            >
              {isEmbedded ? 'View saved listings' : 'Browse listings'}
            </Button>
          </NotFoundCard>
        </PageStack>
      </AppLayout>
    )
  }

  const building = state.building
  const heroImage = building.imageUrls[state.activeImageIndex]
  const buildingStatus = getBuildingStatusLabel(building.availabilitySummary)
  const bedsLine = formatBuildingBeds(building)

  return (
    <AppLayout contentMaxWidth="wide">
      <PageStack>
        <BackLink to={backTo}>{backLabel}</BackLink>

        <GalleryRoot>
          <MainImageFrame>
            {heroImage !== undefined ? (
              <MainImage
                src={heroImage}
                alt={building.buildingName}
                loading="lazy"
              />
            ) : (
              <ImagePlaceholder aria-hidden="true">
                <Icon icon={faCity} label="No photo" />
              </ImagePlaceholder>
            )}
          </MainImageFrame>
          {building.imageUrls.length > 1 ? (
            <ThumbnailRow>
              {building.imageUrls.map((url, index) => (
                <Thumbnail
                  key={url}
                  type="button"
                  isActive={state.activeImageIndex === index}
                  onClick={() =>
                    setState(previous => ({
                      ...previous,
                      activeImageIndex: index,
                    }))
                  }
                  aria-label={`Show photo ${(index + 1).toString()}`}
                >
                  <ThumbnailImage src={url} alt="" />
                </Thumbnail>
              ))}
            </ThumbnailRow>
          ) : null}
        </GalleryRoot>

        <HeaderRow>
          <HeaderInfo>
            <Title>{building.buildingName}</Title>
            <Address>
              {building.compiledAddress !== ''
                ? building.compiledAddress
                : building.city}
            </Address>
          </HeaderInfo>
          <HeaderActions>
            <MatchScoreBadge score={building.matchScore} size="md" withLabel />
            <StatusBadge tone={building.availabilitySummary}>
              {buildingStatus}
            </StatusBadge>
          </HeaderActions>
        </HeaderRow>

        <PrimaryRow>
          <PriceBlock>{formatBuildingPriceRange(building)}</PriceBlock>
          <MetaPills>
            <MetaPill>
              {building.matchingListingsCount} matching listing
              {building.matchingListingsCount === 1 ? '' : 's'}
            </MetaPill>
            {bedsLine !== '' ? <MetaPill>{bedsLine}</MetaPill> : null}
            {building.propertyManagerName !== '' ? (
              <MetaPill>by {building.propertyManagerName}</MetaPill>
            ) : null}
          </MetaPills>
        </PrimaryRow>

        {building.tourBooking ? (
          <Button type="button" variant="primary" onClick={handleBookTour}>
            Book a tour
          </Button>
        ) : null}

        {building.description !== '' ? (
          <SectionCard>
            <SectionTitle>About this building</SectionTitle>
            <Description>{building.description}</Description>
          </SectionCard>
        ) : null}

        {building.matchingUnits.length > 0 ? (
          <SectionCard>
            <SectionTitle>
              {incomingFilters === null
                ? 'Listings in this building'
                : `Matching listings (${building.matchingUnits.length.toString()})`}
            </SectionTitle>
            <UnitList>
              {building.matchingUnits.map(unit => {
                const isSaved = savedSet.has(unit.inventoryId)
                return (
                  <UnitRow key={unit.inventoryId}>
                    <UnitInfo>
                      <UnitTitle>
                        {unit.unitName !== ''
                          ? unit.unitName
                          : unit.unitTypeName || 'Unit'}
                      </UnitTitle>
                      <UnitMeta>{formatUnitBedsBaths(unit)}</UnitMeta>
                      <UnitPrice>{formatUnitPrice(unit)}</UnitPrice>
                    </UnitInfo>
                    <UnitActions>
                      <UnitStatusBadge tone={unit.availability}>
                        {getUnitStatusLabel(unit.availability)}
                      </UnitStatusBadge>
                      <Button
                        type="button"
                        variant="ghost"
                        isLoading={togglingSaveId === unit.inventoryId}
                        onClick={() => {
                          void handleUnitSaveToggle(unit)
                        }}
                      >
                        <Icon icon={faHeart} />{' '}
                        {isSaved === true ? 'Saved' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => handleOpenInquire(unit)}
                      >
                        Inquire
                      </Button>
                    </UnitActions>
                  </UnitRow>
                )
              })}
            </UnitList>
          </SectionCard>
        ) : null}

        {building.otherUnits.length > 0 ? (
          <SectionCard>
            <SectionTitle>
              All other listings in this building (
              {building.otherUnits.length.toString()})
            </SectionTitle>
            <UnitList>
              {building.otherUnits.map(unit => {
                const isSaved = savedSet.has(unit.inventoryId)
                return (
                  <UnitRow key={unit.inventoryId}>
                    <UnitInfo>
                      <UnitTitle>
                        {unit.unitName !== ''
                          ? unit.unitName
                          : unit.unitTypeName || 'Unit'}
                      </UnitTitle>
                      <UnitMeta>{formatUnitBedsBaths(unit)}</UnitMeta>
                      <UnitPrice>{formatUnitPrice(unit)}</UnitPrice>
                    </UnitInfo>
                    <UnitActions>
                      <UnitStatusBadge tone={unit.availability}>
                        {getUnitStatusLabel(unit.availability)}
                      </UnitStatusBadge>
                      <Button
                        type="button"
                        variant="ghost"
                        isLoading={togglingSaveId === unit.inventoryId}
                        onClick={() => {
                          void handleUnitSaveToggle(unit)
                        }}
                      >
                        <Icon icon={faHeart} />{' '}
                        {isSaved === true ? 'Saved' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => handleOpenInquire(unit)}
                      >
                        Inquire
                      </Button>
                    </UnitActions>
                  </UnitRow>
                )
              })}
            </UnitList>
          </SectionCard>
        ) : null}

        {building.amenityNames.length > 0 ? (
          <SectionCard>
            <SectionTitle>Amenities</SectionTitle>
            <TagGrid>
              {building.amenityNames.map(name => (
                <TagChip key={name}>{name}</TagChip>
              ))}
            </TagGrid>
          </SectionCard>
        ) : null}

        {building.featureNames.length > 0 ? (
          <SectionCard>
            <SectionTitle>Features</SectionTitle>
            <TagGrid>
              {building.featureNames.map(name => (
                <TagChip key={name}>{name}</TagChip>
              ))}
            </TagGrid>
          </SectionCard>
        ) : null}

        <StickyInquireBar>
          <InquireSummary>
            <InquireSummaryTitle>{building.buildingName}</InquireSummaryTitle>
            <InquireSummaryMeta>
              {formatBuildingPriceRange(building)} · {building.city}
            </InquireSummaryMeta>
          </InquireSummary>
          <InquireActions>
            <Button
              type="button"
              variant="primary"
              onClick={() =>
                handleOpenInquire(building.matchingUnits[0] ?? null)
              }
              disabled={building.matchingUnits.length === 0}
            >
              Inquire on first match
            </Button>
          </InquireActions>
        </StickyInquireBar>

        <InquireLauncher
          inventoryId={state.inquireUnit?.inventoryId ?? ''}
          isOpen={state.isInquireOpen}
          onClose={handleCloseInquire}
        />
      </PageStack>
    </AppLayout>
  )
}

export default DiscoverDetailPage
